import { BoardID } from 'BoardID';
import { DEFAULT_SETTINGS } from 'Constants';
import { BoardParams, BoardType, Settings, Units } from 'Types';

import BoardBase from 'boardClasses/BoardBase';
import { createBoard, getCtor } from 'boardClasses/BoardFactory';
import { isEqual } from 'utils/utils';

type BoardParsedJSON<T extends BoardType = BoardType> = {
  id: BoardID;
  name: string;
  type: T;
  data: BoardParams<T>;
  units?: Units;
};

const CUSTOM_BOARDS_KEY = 'customPatterns';

export function fromLocalStorageSettings(
  lsSettings: Partial<Settings>
): Settings {
  return {
    ...DEFAULT_SETTINGS,
    ...lsSettings,
    random: {
      ...DEFAULT_SETTINGS.random,
      ...lsSettings.random,
    },
    materialCosts: {
      ...DEFAULT_SETTINGS.materialCosts,
      ...lsSettings.materialCosts,
    },
  };
}

// remove default settings
export function toLocalStorageSettings(settings: Settings): Partial<Settings> {
  return (Object.keys(settings) as Array<keyof Settings>).reduce(
    (nonDefaultSettings, key) => {
      if (!isEqual(settings[key], DEFAULT_SETTINGS[key])) {
        return {
          ...nonDefaultSettings,
          [key]: settings[key],
        };
      }

      return nonDefaultSettings;
    },
    {}
  );
}

export function getLocalBoards(): Array<BoardBase<BoardType>> {
  const localBoardsLS: Array<BoardParsedJSON> = JSON.parse(
    localStorage.getItem(CUSTOM_BOARDS_KEY) || '[]'
  );

  return localBoardsLS.map(({ id, name, data, type, units }) =>
    createBoard(
      getCtor(type),
      id,
      name,
      data,
      // Assume all boards before metric support are in inches
      units || Units.IN
    )
  );
}

export function setLocalBoards(boards: Array<BoardParsedJSON>) {
  localStorage.setItem(CUSTOM_BOARDS_KEY, JSON.stringify(boards));
}

export function saveBoardToLS(board: BoardBase) {
  const localBoards = getLocalBoards();
  const boardIndex = localBoards.findIndex(({ id }) => id === board.id);

  if (boardIndex === -1) {
    localBoards.push(board);
  } else {
    localBoards[boardIndex] = board;
  }

  setLocalBoards(localBoards.map(toLSBoard));
}

export function deleteBoardFromLS(boardID: BoardID) {
  const localBoards = getLocalBoards();
  setLocalBoards(localBoards.filter(({ id }) => id !== boardID).map(toLSBoard));
}

function toLSBoard(board: BoardBase): BoardParsedJSON {
  return {
    id: board.id,
    name: board.name,
    data: board.params,
    type: board.type,
    units: board.units,
  };
}

export function writeStateToLS(boards: Array<BoardBase>) {
  setLocalBoards(boards.map(toLSBoard));
}
